/**
 * MCP ROI Dashboard (View 1)
 *
 * Comprehensive ROI dashboard for MCP sponsors showing:
 * - Enrollment performance vs targets
 * - Cost per enrollment
 * - Healthcare delivery savings
 * - Budget utilization
 * - ROI metrics
 * - Milestone-level breakdown
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getWeaveinApiUrl } from '@/src/config';
import ROIModeler from './ROIModeler';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface ProgramROIConfig {
  enrollment_target: number;
  total_budget: number;
  cost_per_enrollment_target: number;
  avg_pmpm_rate: number;
  projected_annual_revenue: number;
  baseline_pmpm_cost: number;
  target_pmpm_cost: number;
  cost_reduction_percentage: number;
  projected_annual_savings: number;
  target_retention_rate_6mo: number;
  target_retention_rate_12mo: number;
  target_hra_completion_rate: number;
}

interface ProgramROIActuals {
  actual_enrollments: number;
  actual_active_6mo: number;
  actual_active_12mo: number;
  actual_hra_completions: number;
  actual_cbo_payouts: number;
  actual_member_payouts: number;
  actual_total_program_cost: number;
  actual_medicaid_revenue: number;
  actual_cost_savings: number;
  actual_cost_per_enrollment: number;
  actual_retention_rate_6mo: number;
  actual_retention_rate_12mo: number;
  actual_hra_completion_rate: number;
  total_value_created: number;
  roi_percentage: number;
  roi_multiple: number;
  as_of_date: string;
}

interface MilestoneROIContribution {
  milestone_id: number;
  milestone_name: string;
  milestone_type: string;
  target_completions: number;
  actual_completions: number;
  completion_rate: number;
  cbo_payout_per_completion: number;
  member_payout_per_completion: number;
  total_cbo_payouts: number;
  total_member_payouts: number;
  total_milestone_cost: number;
  estimated_value_per_completion: number;
  total_value_created: number;
  milestone_roi_percentage: number;
  milestone_roi_multiple: number;
}

interface MCPROIData {
  program_id: number;
  program_name: string;
  sponsor_id: number;
  sponsor_name: string;
  config: ProgramROIConfig;
  actuals: ProgramROIActuals;
  milestone_contributions: MilestoneROIContribution[];
  summary: {
    enrollment_target: number;
    enrollment_actual: number;
    enrollment_achievement_rate: number;
    total_budget: number;
    total_spent: number;
    budget_utilization_rate: number;
    cost_per_enrollment_target: number;
    cost_per_enrollment_actual: number;
    total_value_created: number;
    roi_percentage: number;
    roi_multiple: number;
  };
  as_of_date: string;
}

interface ClinicalOutcomes {
  program_id: number;
  actuals: {
    actual_ed_visits: number;
    actual_hospital_admits: number;
    actual_cost_savings: number;
    as_of_date: string;
  } | null;
  by_domain: { domain: string | null; referrals: number; ed_avoided: number; savings: number }[];
}

interface MCPROIDashboardProps {
  programId: number;
  sponsorId?: number;
}

const MCPROIDashboard: React.FC<MCPROIDashboardProps> = ({ programId, sponsorId }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [data, setData] = useState<MCPROIData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'modeler' | 'clinical'>('dashboard');
  const [clinical, setClinical] = useState<ClinicalOutcomes | null>(null);
  const [clinicalLoading, setClinicalLoading] = useState(false);
  const [clinicalEnabled, setClinicalEnabled] = useState(true);

  useEffect(() => {
    fetchROIData();
  }, [programId]);

  // Lazy-load clinical outcomes the first time the tab is opened. A 403 means the
  // clinical<->social handoff isn't enabled for this org — hide the tab quietly.
  useEffect(() => {
    if (activeTab !== 'clinical' || clinical || clinicalLoading) return;
    const fetchClinical = async () => {
      try {
        setClinicalLoading(true);
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(
          getWeaveinApiUrl(`/api/handoff/programs/${programId}/clinical-outcomes`),
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            },
          },
        );
        setClinical(response.data);
      } catch (err: any) {
        if (err.response?.status === 403) setClinicalEnabled(false);
      } finally {
        setClinicalLoading(false);
      }
    };
    void fetchClinical();
  }, [activeTab, clinical, clinicalLoading, programId]);

  const fetchROIData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        getWeaveinApiUrl(`/api/roi/programs/${programId}/mcp-view`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || ''
          }
        }
      );
      setData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load ROI data');
      console.error('Error fetching ROI data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number): string => {
    return `${(value ?? 0).toFixed(1)}%`;
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getROIBadgeColor = (roiMultiple: number): string => {
    if (roiMultiple >= 5) return 'bg-green-100 text-green-800';
    if (roiMultiple >= 3) return 'bg-blue-100 text-blue-800';
    if (roiMultiple >= 1) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={fetchROIData}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          {t('sponsors.retry', 'Retry')}
        </button>
      </div>
    );
  }

  if (!data) {
    return <div>{t('sponsors.no_roi_data_available', 'No ROI data available')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-6 py-3 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('sponsors.performance_dashboard', 'Performance Dashboard')}
            </button>
            <button
              onClick={() => setActiveTab('modeler')}
              className={`px-6 py-3 border-b-2 font-medium text-sm ${
                activeTab === 'modeler'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('sponsors.roi_modeler', 'ROI Modeler')}
            </button>
            {clinicalEnabled && (
              <button
                onClick={() => setActiveTab('clinical')}
                className={`px-6 py-3 border-b-2 font-medium text-sm ${
                  activeTab === 'clinical'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('sponsors.clinical_outcomes', 'Clinical Outcomes')}
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Clinical Outcomes Tab — utilization avoided + $ cost-offset from the handoff */}
      {activeTab === 'clinical' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('sponsors.clinical_outcomes', 'Clinical Outcomes')}
            </h3>
            <p className="text-sm text-gray-500">
              ED &amp; inpatient utilization avoided and the dollar cost-offset attributed to social
              interventions delivered through this program.
              {clinical?.actuals?.as_of_date && ` As of ${fmt(clinical.actuals.as_of_date)}.`}
            </p>
          </div>

          {clinicalLoading && <p className="text-sm text-gray-500">Loading clinical outcomes…</p>}

          {!clinicalLoading && !clinical?.actuals && (
            <p className="text-sm text-gray-500">
              No attributed outcomes yet. Numbers appear once utilization claims are ingested and the
              nightly attribution runs.
            </p>
          )}

          {!clinicalLoading && clinical?.actuals && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                  {formatNumber(clinical.actuals.actual_ed_visits)}
                </div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">
                  Post-intervention ED visits
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                  {formatNumber(clinical.actuals.actual_hospital_admits)}
                </div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">
                  Post-intervention admits
                </div>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <div className="text-2xl font-bold text-emerald-700 tabular-nums">
                  {formatCurrency(clinical.actuals.actual_cost_savings)}
                </div>
                <div className="text-xs uppercase tracking-wide text-emerald-700 mt-1">
                  Attributed cost-offset
                </div>
              </div>
            </div>
          )}

          {!clinicalLoading && (clinical?.by_domain?.length ?? 0) > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="py-2 pr-4 font-medium">SDOH domain</th>
                    <th className="py-2 pr-4 font-medium text-right">Referrals</th>
                    <th className="py-2 pr-4 font-medium text-right">ED avoided</th>
                    <th className="py-2 font-medium text-right">Cost-offset</th>
                  </tr>
                </thead>
                <tbody>
                  {clinical.by_domain.map((row) => (
                    <tr key={row.domain ?? 'unmapped'} className="border-b border-gray-100">
                      <td className="py-2 pr-4">{row.domain ?? 'Unmapped'}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{formatNumber(row.referrals)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{formatNumber(row.ed_avoided)}</td>
                      <td className="py-2 text-right tabular-nums">{formatCurrency(row.savings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ROI Modeler Tab */}
      {activeTab === 'modeler' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <ROIModeler
            programId={programId}
            programType={data.program_name.includes('ECM') ? 'CalAIM_ECM' : 'CalAIM_CS'}
            sponsorId={sponsorId || 1}
            initialConfig={{
              enrollment_target: data.config.enrollment_target,
              total_budget: data.config.total_budget,
              avg_pmpm_rate: data.config.avg_pmpm_rate,
              baseline_pmpm_cost: data.config.baseline_pmpm_cost,
              target_pmpm_cost: data.config.target_pmpm_cost,
              target_retention_rate_6mo: data.config.target_retention_rate_6mo,
              target_retention_rate_12mo: data.config.target_retention_rate_12mo,
            }}
          />
        </div>
      )}

      {/* Dashboard Tab Content */}
      {activeTab === 'dashboard' && (
        <>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{data.program_name}</h1>
            <p className="text-gray-600 mt-1">{data.sponsor_name}</p>
            <p className="text-sm text-gray-500 mt-1">
              As of {fmt.short(data.as_of_date)}
            </p>
          </div>
          <div className="text-right">
            <div className={`inline-flex items-center px-4 py-2 rounded-full ${getROIBadgeColor(data.summary.roi_multiple)}`}>
              <span className="text-2xl font-bold">
                {(data.summary?.roi_multiple ?? 0).toFixed(1)}x ROI
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {formatPercent(data.summary.roi_percentage)} Return
            </p>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Enrollments */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('sponsors.enrollments', 'Enrollments')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {formatNumber(data.summary.enrollment_actual)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                of {formatNumber(data.summary.enrollment_target)} target
              </p>
            </div>
            <div className="text-right">
              <span className={`inline-block px-2 py-1 rounded text-sm font-semibold ${
                data.summary.enrollment_achievement_rate >= 100 ? 'bg-green-100 text-green-800' :
                data.summary.enrollment_achievement_rate >= 75 ? 'bg-blue-100 text-blue-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {formatPercent(data.summary.enrollment_achievement_rate)}
              </span>
            </div>
          </div>
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor(data.summary.enrollment_achievement_rate)}`}
                style={{ width: `${Math.min(data.summary.enrollment_achievement_rate, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Cost Per Enrollment */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('sponsors.cost_per_enrollment', 'Cost Per Enrollment')}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {formatCurrency(data.summary.cost_per_enrollment_actual)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Target: {formatCurrency(data.summary.cost_per_enrollment_target)}
          </p>
          <div className="mt-3">
            {data.summary.cost_per_enrollment_actual <= data.summary.cost_per_enrollment_target ? (
              <span className="text-green-600 text-sm font-medium">✓ On Target</span>
            ) : (
              <span className="text-yellow-600 text-sm font-medium">
                {formatCurrency(data.summary.cost_per_enrollment_actual - data.summary.cost_per_enrollment_target)} over
              </span>
            )}
          </div>
        </div>

        {/* Budget Utilization */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('sponsors.budget_utilization', 'Budget Utilization')}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {formatPercent(data.summary.budget_utilization_rate)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {formatCurrency(data.summary.total_spent)} of {formatCurrency(data.summary.total_budget)}
          </p>
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor(data.summary.budget_utilization_rate)}`}
                style={{ width: `${Math.min(data.summary.budget_utilization_rate, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Total Value Created */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('sponsors.total_value_created', 'Total Value Created')}</p>
          <p className="text-3xl font-bold text-green-600 mt-2">
            {formatCurrency(data.summary.total_value_created)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {t('sponsors.revenue_savings_costs', 'Revenue + Savings - Costs')}
          </p>
          <div className="mt-3">
            <span className="text-green-600 text-sm font-medium">
              ↑ {formatPercent(data.summary.roi_percentage)} ROI
            </span>
          </div>
        </div>
      </div>

      {/* Financial Breakdown */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('sponsors.financial_performance', 'Financial Performance')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Revenue */}
          <div>
            <h3 className="text-sm font-medium text-gray-600 mb-3">{t('sponsors.revenue', 'Revenue')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.medicaid_capitation', 'Medicaid Capitation')}</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(data.actuals.actual_medicaid_revenue)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.projected_annual', 'Projected (Annual)')}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatCurrency(data.config.projected_annual_revenue)}
                </span>
              </div>
            </div>
          </div>

          {/* Costs */}
          <div>
            <h3 className="text-sm font-medium text-gray-600 mb-3">{t('sponsors.program_costs', 'Program Costs')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.cbo_payouts', 'CBO Payouts')}</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(data.actuals.actual_cbo_payouts)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.member_payouts', 'Member Payouts')}</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(data.actuals.actual_member_payouts)}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{t('sponsors.total_cost', 'Total Cost')}</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {formatCurrency(data.actuals.actual_total_program_cost)}
                </span>
              </div>
            </div>
          </div>

          {/* Savings */}
          <div>
            <h3 className="text-sm font-medium text-gray-600 mb-3">{t('sponsors.healthcare_savings', 'Healthcare Savings')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.cost_reduction', 'Cost Reduction')}</span>
                <span className="text-sm font-semibold text-green-600">
                  {formatCurrency(data.actuals.actual_cost_savings)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.projected_annual_1', 'Projected (Annual)')}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatCurrency(data.config.projected_annual_savings)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.pmpm_reduction', 'PMPM Reduction')}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatPercent(data.config.cost_reduction_percentage)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('sponsors.performance_metrics', 'Performance Metrics')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* 6-Month Retention */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">6-Month Retention</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {formatPercent(data.actuals.actual_retention_rate_6mo)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor((data.actuals.actual_retention_rate_6mo / data.config.target_retention_rate_6mo) * 100)}`}
                style={{ width: `${Math.min((data.actuals.actual_retention_rate_6mo / data.config.target_retention_rate_6mo) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Target: {formatPercent(data.config.target_retention_rate_6mo)}
            </p>
          </div>

          {/* 12-Month Retention */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">12-Month Retention</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {formatPercent(data.actuals.actual_retention_rate_12mo)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor((data.actuals.actual_retention_rate_12mo / data.config.target_retention_rate_12mo) * 100)}`}
                style={{ width: `${Math.min((data.actuals.actual_retention_rate_12mo / data.config.target_retention_rate_12mo) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Target: {formatPercent(data.config.target_retention_rate_12mo)}
            </p>
          </div>

          {/* HRA Completion */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('sponsors.hra_completion', 'HRA Completion')}</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {formatPercent(data.actuals.actual_hra_completion_rate)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getProgressColor((data.actuals.actual_hra_completion_rate / data.config.target_hra_completion_rate) * 100)}`}
                style={{ width: `${Math.min((data.actuals.actual_hra_completion_rate / data.config.target_hra_completion_rate) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Target: {formatPercent(data.config.target_hra_completion_rate)}
            </p>
          </div>
        </div>
      </div>

      {/* Milestone Breakdown */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('sponsors.milestone_performance', 'Milestone Performance')}</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('sponsors.milestone', 'Milestone')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('sponsors.progress', 'Progress')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('sponsors.cbo_payouts_1', 'CBO Payouts')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('sponsors.member_payouts_1', 'Member Payouts')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('sponsors.total_cost_1', 'Total Cost')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('sponsors.value_created', 'Value Created')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ROI
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
              {data.milestone_contributions.map((milestone) => (
                <tr key={milestone.milestone_id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{milestone.milestone_name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{milestone.milestone_type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1 mr-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${getProgressColor(milestone.completion_rate)}`}
                            style={{ width: `${Math.min(milestone.completion_rate, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm text-gray-900 dark:text-white">{formatPercent(milestone.completion_rate)}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatNumber(milestone.actual_completions)} / {formatNumber(milestone.target_completions)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                    {formatCurrency(milestone.total_cbo_payouts)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                    {formatCurrency(milestone.total_member_payouts)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-white">
                    {formatCurrency(milestone.total_milestone_cost)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-green-600">
                    {formatCurrency(milestone.total_value_created)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getROIBadgeColor(milestone.milestone_roi_multiple)}`}>
                      {(milestone.milestone_roi_multiple ?? 0).toFixed(1)}x
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default MCPROIDashboard;
